// ── A YIELD MAP FROM ANY MONITOR, BY HAND ────────────────────────────────────
//
// Kevin, 2026-10-01: *"can we figure out a way to import yield data from any yield monitor
// through a manual upload? Since not everyone uses John Deere, if we can get their yield map
// shapefile, can we import that yield data and have it mapped in field mapper, display yield
// map heat map, and then also use the imported yield data for production and claims tracking?"*
//
// Measured the same day: **208 of 233 CY2026 farms have no John Deere connection**, carrying
// 194,015 insured acres, and **99% of those acres already have a field boundary** — so the
// ground can be placed; only the yield is missing. The two farms that have silage appraisals
// are both in that 208, which is why the appraised-vs-combined comparison reads 0 on the one
// screen built to show it.
//
// ⚠⚠ THE RULE THIS WHOLE MODULE EXISTS FOR: **NOTHING ABOUT A FOREIGN FILE IS INFERRED FROM
// WHAT ITS NUMBERS LOOK LIKE.** A DBF column of numbers states no unit, no moisture basis and
// no crop. 180 could be bu/ac of corn, 180 could be a dry-matter ton figure times ten, and
// both look exactly as plausible. Inferring either is the failure rule zero opens with, and
// the number lands on a production report a grower signs.
//
// So the importer READS THE FILE'S OWN COLUMNS and ASKS. A candidate list survives only as a
// pre-selection SUGGESTION, never as the thing that decides — and the suggestion is labelled
// as one on screen.
//
// ⚠ AND THE SUGGESTION LIST CANNOT BE WRITTEN FROM MEMORY. No non-John-Deere export has been
// seen here. John Deere's own list is `VRYIELDVOL` then `/yield/i`; run that against a column
// named `Yld_Vol_Dr` and it finds NOTHING, because `Yld` is not `yield` — the operator is then
// told there is no usable yield column in a file whose yield column is right there. Every
// pattern below is therefore marked with where it came from, and `OBSERVED` means observed in
// a file this repo has actually parsed. Adding a pattern because it seems likely is how a
// wrong column gets picked silently.

"use strict";

const up = (v) => String(v == null ? "" : v).trim().toUpperCase();
const k = (v) => String(v == null ? "" : v).trim();

// ── what the stored grid may say ────────────────────────────────────────────
// ⚠ THESE ARE THE UNITS `jd_yield_grids.unit` ALREADY HOLDS, measured 2026-10-01 over its ~35.2k rows:
// yield bu/ac ~16.8k · DM ton/ac 251 · ton/ac 8; seed_rate seeds/ac ~13.9k · lb/ac ~2.9k.
// ⚠ ROUNDED AND DATED on purpose: the precompute cron builds grids continuously, so an exact
// count written here is stale within the hour — three of these were, inside one session. What
// is load-bearing is that the vocabulary is SMALL and that these are all of it.
// A manual import must land in the SAME vocabulary or every consumer — the Compare lens, the
// Accounted-for layer, the production report — has to learn a second one, and this repo has
// already paid for two surfaces meaning different things by one column.
const YIELD_UNITS = Object.freeze([
  { id: "bu/ac", label: "bushels per acre", note: "corn, soybeans, wheat, oats, barley" },
  { id: "ton/ac", label: "tons per acre (as harvested)", note: "silage and forage AT ITS HARVESTED MOISTURE" },
  { id: "DM ton/ac", label: "tons per acre, dry matter", note: "silage and forage converted to a dry-matter basis" },
  { id: "lb/ac", label: "pounds per acre", note: "" },
]);

// ⚠ MOISTURE IS A SEPARATE QUESTION FROM THE UNIT, AND IT IS THE ONE A READER FORGETS.
// John Deere's VRYIELDVOL is already adjusted to the crop's market moisture. Another monitor's
// column may be WET — and wet bushels reported as dry OVERSTATE production, which on a claim
// moves money in the direction that hurts the grower.
//
// There is no third state. "I don't know" is not a basis we can store, because every consumer
// of `bu_mean` would then have to carry an unknown through its arithmetic — so an unanswered
// moisture basis REFUSES the import rather than guessing one.
const MOISTURE_BASIS = Object.freeze([
  { id: "market", label: "already adjusted to market moisture",
    note: "the monitor's own dry/standard yield column — what John Deere's VRYIELDVOL is" },
  { id: "wet", label: "as harvested, not adjusted",
    note: "needs the harvest moisture to be useful as reported production" },
]);

// ── suggestions, and ONLY suggestions ───────────────────────────────────────
// `source` is the honest half of each row. OBSERVED = seen in a file this repo has parsed.
// GENERIC = a word that means what it says in English, which is a far weaker claim than
// knowing a vendor writes it.
const YIELD_HINTS = Object.freeze([
  { re: /^VRYIELDVOL$/i, source: "OBSERVED — John Deere, ~8.5k grids" },
  { re: /^VRYIELDMAS$/i, source: "OBSERVED — John Deere, ~6.0k grids" },
  { re: /yield/i, source: "GENERIC — the word itself" },
  { re: /^yld/i, source: "GENERIC — the common abbreviation" },
  { re: /bu.?ac|bushel/i, source: "GENERIC — the unit in the name" },
]);
const SEED_HINTS = Object.freeze([
  { re: /^AppliedRate$/i, source: "OBSERVED — John Deere, ~13.0k grids" },
  { re: /^TargetRate$/i, source: "OBSERVED — John Deere, ~2.3k grids" },
  { re: /applied.?rate/i, source: "GENERIC" },
  { re: /seed.?rate|seeding.?rate|population/i, source: "GENERIC" },
  { re: /rate/i, source: "GENERIC — any rate column" },
]);
// Columns that are never the measurement, however well they match. Measured against the
// John Deere exports this repo parses: a file carries Moisture beside its yield, and
// `/yield/i` does not match it — but `/rate/i` matches `FlowRate` and `SwathRate`, which are
// machine telemetry rather than what went on or came off the ground.
const NEVER = Object.freeze([
  /moist/i, /elev/i, /speed/i, /heading/i, /swath/i, /width/i, /distance/i,
  /^time$/i, /^date$/i, /sectionid/i, /^pass/i, /flow/i, /^area/i, /^acres?$/i,
]);

// ── the monitors this has to accept ─────────────────────────────────────────
// Kevin, 2026-10-01: *"We need to be able to accept precision planting, Trimble, ag leader,
// case ih, etc"* — so the list is HIS, and `etc` is the operative word: anything may be typed.
//
// ⚠⚠ NAMING A MONITOR IS NOT KNOWING ITS FILE. This list exists so the operator can say which
// box the card came out of, and for NOTHING else — no column is pre-selected from it, no unit
// is implied by it. That distinction is the whole discipline here: the moment a vendor name
// starts deciding what a column means, we are back to inventing a format we have never seen.
//
// What the name IS for is the line below it: once a file from a monitor has been imported, its
// columns have been OBSERVED, and the next file from that monitor can be suggested from
// evidence rather than from a guess. That is how the list gets built — from files that were
// actually parsed, one grower at a time.
const MONITORS = Object.freeze([
  "John Deere", "Precision Planting", "Trimble", "Ag Leader", "Case IH",
  "Climate FieldView", "CNH / New Holland", "Raven", "Topcon", "AGCO / Fendt",
  // Always last, always available — the list is a convenience, never a gate.
  "Other",
]);

/**
 * learnedHints(imports) — turn past imports into suggestions for the next file.
 *
 * ⚠ THIS IS THE ONLY SANCTIONED WAY THE HINT LIST GROWS. A pattern added because a vendor
 * "probably" names a column something is a story; a pattern added because a file carrying it
 * was imported on this book is a reading. Each learned hint says which monitor it came from
 * and how many files backed it, so a one-off import never reads like an established format.
 *
 * `imports`: [{ monitor, column, metric, unit, moisture_basis }] — rows this app wrote.
 */
function learnedHints(imports = [], metric = "yield", monitor = null) {
  const byCol = new Map();
  for (const r of imports) {
    if (!r || k(r.column) === "") continue;
    if (k(r.metric) && k(r.metric) !== k(metric)) continue;
    const key = up(r.column) + "|" + up(r.monitor);
    const e = byCol.get(key) || { column: k(r.column), monitor: k(r.monitor), n: 0, units: new Map(), bases: new Map() };
    e.n++;
    if (k(r.unit)) e.units.set(k(r.unit), (e.units.get(k(r.unit)) || 0) + 1);
    if (k(r.moisture_basis)) e.bases.set(k(r.moisture_basis), (e.bases.get(k(r.moisture_basis)) || 0) + 1);
    byCol.set(key, e);
  }
  const top = (m) => {
    // ⚠ A SPLIT ANSWER IS NO ANSWER. If past imports of the same column disagreed about the
    // unit, suggesting either one would present a coin flip as a memory — so it suggests
    // nothing and the operator answers again, which is the only honest move.
    const sorted = [...m.entries()].sort((a, b) => b[1] - a[1]);
    if (!sorted.length) return null;
    if (sorted.length > 1 && sorted[0][1] === sorted[1][1]) return null;
    return sorted[0][0];
  };
  return [...byCol.values()].map((e) => ({
    re: new RegExp("^" + e.column.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "$", "i"),
    // A hint from the SAME monitor is stronger evidence than one from another box that
    // happened to use the same column name.
    sameMonitor: !!(monitor && up(e.monitor) === up(monitor)),
    unit: top(e.units), moistureBasis: top(e.bases),
    source: `OBSERVED — ${e.monitor || "an earlier import"}, ${e.n} file${e.n === 1 ? "" : "s"} on this book`,
  })).sort((a, b) => (b.sameMonitor ? 1 : 0) - (a.sameMonitor ? 1 : 0));
}

/**
 * suggestColumn(fields, metric) — rank a DBF's OWN columns, best guess first.
 *
 * Takes the columns the file actually has. Returns every one of them, in the order a human
 * should consider them, each carrying WHY it is suggested — so the operator is choosing
 * between named candidates rather than trusting a hidden list.
 *
 * ⚠ IT NEVER RETURNS A DECISION. There is no `picked` field, by construction: a caller that
 * wants to auto-select has to write that itself somewhere a checker can see it.
 */
function suggestColumn(fields = [], metric = "yield", learned = []) {
  // ⚠ LEARNED HINTS COME FIRST, because they are the only ones backed by a file this book has
  // actually parsed. The built-in list is John Deere plus a handful of English words; a column
  // an operator confirmed on a Trimble export last month outranks both.
  const hints = [...(learned || []), ...(metric === "seed_rate" ? SEED_HINTS : YIELD_HINTS)];
  const out = [];
  for (const f of fields) {
    const name = k(f && f.name);
    if (!name) continue;
    // ⚠ NUMERIC COLUMNS ONLY. A character column holding "182.4" is a label the monitor wrote,
    // not a measurement it took, and binning it would read a string as a yield.
    const numeric = up(f.type) === "N" || up(f.type) === "F";
    const blocked = NEVER.find((r) => r.test(name));
    const hit = hints.findIndex((h) => h.re.test(name));
    out.push({
      name, type: f.type, len: f.len,
      // rank: lower is better. A blocked column still APPEARS — the operator may know
      // something the list does not — it simply cannot be suggested.
      rank: blocked ? 900 : !numeric ? 800 : hit < 0 ? 500 : hit,
      why: blocked ? `not a measurement — the name matches ${blocked}`
         : !numeric ? `column type ${f.type}, not numeric`
         : hit < 0 ? "no hint matched — pick it only if you know this file"
         : hints[hit].source,
      suggested: !blocked && numeric && hit >= 0,
      // What this column meant LAST time, where a past import answered it. Carried so the
      // operator can confirm rather than re-derive — and null, never a default, where past
      // imports disagreed or there were none.
      lastUnit: !blocked && numeric && hit >= 0 ? (hints[hit].unit || null) : null,
      lastMoistureBasis: !blocked && numeric && hit >= 0 ? (hints[hit].moistureBasis || null) : null,
    });
  }
  return out.sort((a, b) => a.rank - b.rank || a.name.localeCompare(b.name));
}

/**
 * importGaps(draft) — everything still missing before a shapefile may be imported.
 *
 * Returns a list, never a boolean, because WHICH answer is missing decides who can supply it:
 * a crop is the agent's to know, a unit is the grower's, and a column needs somebody looking
 * at the file.
 *
 * ⚠ EVERY ONE OF THESE IS A REFUSAL, NOT A WARNING. A yield grid with a guessed unit is worse
 * than no grid: it renders on a heat map, averages into a mean, and reaches a production
 * report, with nothing anywhere saying the unit was assumed.
 */
function importGaps(d = {}) {
  const gaps = [];
  if (!k(d.farmerId)) gaps.push({ field: "farmerId", say: "which farm this file belongs to" });
  if (!k(d.crop)) gaps.push({ field: "crop", say: "which crop was harvested — it decides the unit and the APH line this can ever feed" });
  if (!k(d.cropYear)) gaps.push({ field: "cropYear", say: "which crop year" });
  if (!k(d.operationDate)) gaps.push({ field: "operationDate", say: "the date of the pass — a harvest with no date cannot be scoped to a crop year" });
  if (!k(d.metric)) gaps.push({ field: "metric", say: "whether this is a harvest or a planting pass" });
  if (!k(d.column)) gaps.push({ field: "column", say: "which column in the file holds the measurement" });
  // ⚠ THE STORED FILE PATH IS A REQUIREMENT, NOT A CONVENIENCE — see manualOperationId. The
  // CLU attribution is DELETED by operation id before it is rewritten, so an import with no
  // stable id of its own would wipe the attribution of every other hand-imported grid.
  if (!k(d.storagePath)) gaps.push({ field: "storagePath", say: "where the uploaded file is stored — it is what gives this import an identity of its own" });
  if (!k(d.unit)) gaps.push({ field: "unit", say: "what unit that column is in — nothing in the file states it" });
  else if (!YIELD_UNITS.some((u) => u.id === d.unit)) {
    gaps.push({ field: "unit", say: `"${d.unit}" is not a unit this app stores — it would be the only one of its kind` });
  }
  // Moisture only applies to a harvest. A planting rate has no moisture basis, and asking for
  // one would be a question with no true answer.
  if (k(d.metric) === "yield") {
    if (!k(d.moistureBasis)) {
      gaps.push({ field: "moistureBasis", say: "whether that column is already moisture-adjusted — wet bushels reported as dry overstate production" });
    } else if (!MOISTURE_BASIS.some((m) => m.id === d.moistureBasis)) {
      gaps.push({ field: "moistureBasis", say: `"${d.moistureBasis}" is not a basis this app stores` });
    }
  }
  // ⚠ AND A CROP YEAR THAT CONTRADICTS THE DATE IS REFUSED HERE, in the rule, so the screen
  // and the write path cannot disagree about it. Kevin, about to import a batch of OLD maps:
  // "I want to make sure dont go into 2026 data". A box nobody changed is exactly how that
  // happens, so a gap the fall-seeding rollover cannot explain stops the import.
  const clash = cropYearConflict(d);
  if (clash && clash.severity === "refuse") gaps.push({ field: "cropYear", say: clash.say });
  return gaps;
}

// ── WHICH CROP YEAR AN OLD MAP BELONGS TO ───────────────────────────────────
// Kevin, 2026-10-01, about to import a batch of OLD yield maps: *"will the importer be smart
// enough to pick up crop year? Because I am about to import some old yield maps that I want to
// make sure dont go into 2026 data"*.
//
// ⚠⚠ IT WAS NOT, AND THE DEFAULT WAS THE DANGER. The panel pre-filled the crop year with the
// CURRENT year, so a 2021 map whose date was typed correctly would still have filed as CY2026
// unless somebody noticed the box. A prefilled year is a value nobody entered appearing as
// though they had — the same rule this repo applies to planted acres and to input rates.
//
// So the year is NOT defaulted. It is SUGGESTED from the date of the pass, which is a fact the
// operator supplies, and the suggestion is shown as a derivation they can overrule.
//
// THE RULE, and the one case where it is not simply the calendar year:
//   • a HARVEST belongs to the crop year it was harvested in — corn cut in October 2021 is
//     CY2021, and wheat cut in July 2026 is CY2026 (it was planted in the autumn of 2025, and
//     RMA counts that as the 2026 crop).
//   • a PLANTING of a FALL-SEEDED crop belongs to the NEXT crop year — wheat drilled in
//     October 2025 is the CY2026 crop. This is the same rollover FALL_CROPS carries everywhere
//     else in this app, and getting it wrong files a planting a whole year out.
//
// A spring planting and every harvest are just the calendar year, which is why the fall-seeding
// case is the only branch.
const FALL_SEEDED = ["WHEAT", "BARLEY", "TRITICALE", "RYE", "SPELT"];

function suggestCropYear(d = {}) {
  const date = k(d.operationDate);
  const m = /^(\d{4})-(\d{2})-\d{2}$/.exec(date);
  if (!m) return null;                       // no date, no suggestion — never a guess
  const y = Number(m[1]), mon = Number(m[2]);
  const crop = up(d.crop);
  const isFallSeeding = k(d.metric) === "seed_rate"
    && FALL_SEEDED.some((c) => crop.indexOf(c) >= 0)
    && mon >= 8;                             // drilled Aug–Dec is next year's crop
  return {
    year: isFallSeeding ? y + 1 : y,
    why: isFallSeeding
      ? `a fall-seeded ${crop || "crop"} drilled in ${date.slice(0, 7)} is the ${y + 1} crop`
      : `the pass was in ${y}`,
    fallRollover: isFallSeeding,
  };
}

/**
 * cropYearConflict(draft) — does the stated crop year disagree with the date?
 *
 * ⚠ THIS IS THE GUARD KEVIN ASKED FOR, and it REFUSES rather than warns when the two are far
 * apart. An old map filed under the current crop year is not a cosmetic error: it lands in the
 * Accounted-for layer, the production evidence and the claim file for a year it has nothing to
 * do with, and it reads as this season's harvest.
 *
 * A gap of ONE year is a judgement call and is FLAGGED, never allowed and never refused: it is
 * right for a fall-seeded crop filed under the year it was drilled and wrong for everything
 * else, and only the operator knows which. Anything larger cannot be right and is refused with
 * both figures named — because a difference of five years is a box nobody changed, not a
 * decision somebody made.
 *
 * ⚠ THERE WAS A `gap === 1 && s.fallRollover` ALLOWANCE HERE AND IT LET A WRONG YEAR THROUGH.
 * It read as "the rollover is already explained", but `suggestCropYear` has ALREADY rolled the
 * year forward by the time this runs, so a gap of 0 is the operator agreeing with the rollover
 * and a gap of +1 is a year PAST it. Measured: wheat drilled 2025-10-01 (the 2026 crop) filed
 * as CY2027 returned null — no conflict, no flag — which is the defect this whole rule exists
 * to catch, one year out instead of five. Found by mutation testing, not by reading: removing
 * the branch changed no answer the checker was asking about, which is what a dead branch looks
 * like from the outside. The -1 direction (drilled Oct 2025, typed 2025) is the case that is
 * genuinely ambiguous, and `Math.abs(gap) === 1` already flags it.
 */
function cropYearConflict(d = {}) {
  const s = suggestCropYear(d);
  const stated = Number(k(d.cropYear));
  if (!s || !Number.isFinite(stated)) return null;
  const gap = stated - s.year;
  if (gap === 0) return null;
  if (Math.abs(gap) === 1) {
    return { severity: "check", stated, fromDate: s.year,
      say: `the pass is dated ${k(d.operationDate)} but the crop year says ${stated} — that is right for a fall-seeded crop and wrong for everything else` };
  }
  return { severity: "refuse", stated, fromDate: s.year,
    say: `the pass is dated ${k(d.operationDate)} but the crop year says ${stated}, ${Math.abs(gap)} years apart — check the crop year before importing` };
}

/**
 * manualOperationId(draft) — the id a hand-imported pass is stored under.
 *
 * ⚠⚠ THIS IS THE DESTRUCTIVE ONE, and it is not obvious from anything on screen.
 * `computeFtfSplit` and `computeCountySplit` both do
 *
 *     DELETE jd_field_ftf WHERE jd_operation_id = <opId>     …then insert
 *
 * so that id owns the ENTIRE CLU attribution of a pass. The John Deere path passes
 * `String(harv.id)`, which is unique per operation. A manual import has no John Deere
 * operation — and `String(undefined)` is the string **"undefined"**, so every hand-imported
 * grid in the book would share one id and each new import would DELETE the CLU rows of every
 * import before it. Nothing would error; the acres would simply vanish from the Accounted-for
 * layer and the production report, one upload at a time.
 *
 * That is the `replaceRows` shape this repo has already paid for — a delete scoped to a key
 * that turned out not to be a key — so the id is minted here, deliberately, and never defaulted.
 *
 * It is DETERMINISTIC from the file's own storage path, which gives the property that matters:
 * re-importing the same file REPLACES its own rows instead of duplicating them, and re-importing
 * a different file touches nothing. A random id would duplicate on every retry, and a retry is
 * the normal case when an operator fixes the unit and imports again.
 */
function manualOperationId(d = {}) {
  const farmer = k(d.farmerId), file = k(d.storagePath) || k(d.fileName);
  // ⚠ NO FALLBACK. Minting an id from a partial draft is how two different uploads come to
  // share one — and sharing one is the delete above. A caller without both parts has not got
  // enough to store anything safely, and importGaps says so.
  if (!farmer || !file) return null;
  // Namespaced so a manual id can never collide with a John Deere operation id, and so a
  // reader of the raw table can tell at a glance which is which.
  return `manual:${farmer}:${file}`;
}

/**
 * provenanceFor(draft) — what the stored grid should record about where it came from.
 *
 * ⚠ A HAND-IMPORTED GRID MUST BE TELLABLE FROM A MACHINE-SYNCED ONE, FOREVER. `source_host`
 * exists on jd_field_operations for exactly this reason — 60% of that table came from John
 * Deere's SANDBOX and nothing recorded it until somebody asked. The same question will be
 * asked of these grids the first time one disagrees with a scale ticket, and the answer has
 * to be in the row rather than in somebody's memory of which farms were uploaded by hand.
 */
function provenanceFor(d = {}) {
  return {
    source: "manual_upload",
    file_name: k(d.fileName) || null,
    rate_column: k(d.column) || null,
    unit: k(d.unit) || null,
    moisture_basis: k(d.metric) === "yield" ? (k(d.moistureBasis) || null) : null,
    uploaded_by: k(d.uploadedBy) || null,
    // The monitor, where the operator names it. NOT derived from the columns: a column set
    // that looks like a vendor's is not evidence of the vendor, and this field would be the
    // first thing a future candidate list was built from.
    monitor: k(d.monitor) || null,
  };
}

module.exports = { suggestColumn, importGaps, provenanceFor, learnedHints, manualOperationId,
                   suggestCropYear, cropYearConflict,
                   YIELD_UNITS, MOISTURE_BASIS, YIELD_HINTS, SEED_HINTS, NEVER, MONITORS };
